import React, { useEffect, useState, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useDebouncedValue } from '@mantine/hooks';
import {
    Button,
    RadioGroup,
    Radio,
    Group,
    Text,
    Textarea,
    Box,
    Title,
    Divider,
    TextInput,
    Select,
    Avatar,
    ActionIcon,
    Checkbox,
    CheckboxGroup,
    SimpleGrid,
    MultiSelect,
    Stack,
    Grid,
    Container,
    Code,
    Loader,
    Center,
    Popover
} from '@mantine/core';
import { User, Briefcase, Phone, Trash } from 'tabler-icons-react';
import { DatePicker } from '@mantine/dates';
import { useForm, formList } from '@mantine/form';
import externalApi from '../api';
import endpoints from '../api/endpoints';
import { FormList } from '@mantine/form/lib/form-list/form-list';
import { showNotification } from '@mantine/notifications';
import { GraphUser, TicketResponse  } from '../types'

type AllGraphUsers = {
  '@odata.context': string;
  '@odata.nextLink': string;
  value: GraphUser[];
};

type IncidentRequestTicket = {
  ticketSubject: string;
  impact: string;
  urgency: string;
  category: string;
  subcategory: string;
  deviceName: string;
  deviceLocation: string;
  preferredContact: string;
  callbackTime: string;
    recurringIssue: string;
    requestDetails: string;
};

interface ItemProps extends React.ComponentPropsWithoutRef<'div'> {
  image: string;
  label: string;
  value: string;
  description: string;
  initials: string;
}

const SelectItem = forwardRef<HTMLDivElement, ItemProps>(
  ({ image, label, description, initials, ...others }: ItemProps, ref) => (
    <div ref={ref} {...others}>
      <Group noWrap>
        <Avatar src={image}>{initials}</Avatar>
        <div>
          <Text size="sm">{label}</Text>
          <Text size="xs" color="dimmed">
            {description}
          </Text>
        </div>
      </Group>
    </div>
  )
);

const DeviceRequest = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<GraphUser>();
  const [manager, setManager] = useState<GraphUser>();
  const [onBehalfOf, setOnBehalfOf] = useState(false);
  const [loadingState, setLoadingState] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const [userSearchInput, setUserSearchInput] = useState('');
  const [debouncedUserSearchInput] = useDebouncedValue(userSearchInput, 200);
  const [userSelectData, setUserSelectData] = useState<ItemProps[]>([]);

    const ticket: IncidentRequestTicket = {
    ticketSubject: '',
    impact: '',
    urgency: '',
    category: '',
    subcategory: '',
    deviceName: '',
    deviceLocation: '',
    preferredContact: '',
    callbackTime: '',
    recurringIssue: '',
    requestDetails: '',
  };

    const form = useForm<IncidentRequestTicket>({
    initialValues: ticket,
    validate: {},
  });

  async function submitForm() {
    let response: TicketResponse;
    setLoadingState(true);
    if (onBehalfOf) {
      response = await externalApi
        .post(endpoints.submitIncidentRequestTicket.url, {
          json: { ...form.values, requester: manager, submittedBy: user },
        })
        .json();
    } else {
      response = await externalApi
        .post(endpoints.submitIncidentRequestTicket.url, { json: { ...form.values, requester: user } })
        .json();
    }

    if (response.success === true) {
      navigate(`/request-submitted/${response.ticketId}`);
    } else {
      console.log(response);
      setLoadingState(false);
      showNotification({
        title: 'Error Submitting Form',
        color: 'red',
        message: response.response,
      });
    }
  }

  async function loadUser() {
    const me: GraphUser = await externalApi.get(endpoints.graphMe.url).json();
    setUser(me);
  }

  async function loadManager(userPrincipal: string) {
    const graphManager: GraphUser = await externalApi
      .get(
        `${endpoints.graphProfilePhoto.url}/${userPrincipal}?$select=businessPhones,displayName,givenName,jobTitle,mail,mobilePhone,officeLocation,preferredLanguage,surname,department,userPrincipalName,id`
      )
      .json();

    setManager(graphManager);
  }

  async function getUsersFromGraph(displayNameSearch: string) {
    const users: AllGraphUsers = await externalApi
      .get(`${endpoints.graphUsers.url}&$search="displayName:${displayNameSearch}"`, {
        headers: { ConsistencyLevel: 'eventual' },
      })
      .json();

    let searchUsers: ItemProps[] = [];
    users.value.forEach(async (searchUser) => {
      if (searchUser.id !== null) {
        let profilePhotoUrl: string = ''; //await getProfilePhoto(searchUser.id);
        let initials: string =
          searchUser.givenName?.length > 0 ? `${searchUser.givenName?.at(0)}${searchUser.surname?.at(0)}` : '';

        searchUsers.push({
          image: profilePhotoUrl,
          description: searchUser.jobTitle,
          value: searchUser.id,
          label: searchUser.displayName,
          initials: initials,
        });
      }
    });

    setUserSelectData(searchUsers);
  }


    async function loadAllData() {
        loadUser();
        setIsDataLoaded(true);
    }

  useEffect(() => {
    loadAllData();
    if (debouncedUserSearchInput !== '') getUsersFromGraph(debouncedUserSearchInput);
  }, [debouncedUserSearchInput]);

  return (
    <>
      <Title my="sm" order={1}>
        Incident Request
      </Title>

      <form onSubmit={form.onSubmit(() => submitForm())}>
        <Box>
          <Checkbox
            color="blue"
            label="Submitting On Behalf of Manager"
            checked={onBehalfOf}
            onChange={(event) => setOnBehalfOf(event.currentTarget.checked)}
          />

          {!onBehalfOf && (
            <Box>
              <Divider my="sm" label="Requester" labelProps={{ size: 'lg' }} />

              <Stack>
                <Group>
                  <User />
                  <Text>{user?.displayName}</Text>
                </Group>
                <Group>
                  <Briefcase />
                  <Text>{user?.jobTitle},</Text>
                  <Text>{user?.department}</Text>
                </Group>
                <Group>
                  <Phone />
                  <Text>{user?.businessPhones}</Text>
                </Group>
              </Stack>
            </Box>
          )}
          {onBehalfOf && (
            <Box>
              <Divider my="sm" label="Manager" labelProps={{ size: 'lg' }} />
              <Stack>
                <Select
                  searchable
                  label="Select Manager"
                  nothingFound="No Manager Found"
                  itemComponent={SelectItem}
                  onSearchChange={(input: string) => setUserSearchInput(input)}
                  onChange={(value: string) => loadManager(value)}
                  data={userSelectData}
                />
                <Group>
                  <User />
                  <Text>{manager?.displayName}</Text>
                </Group>
                <Group>
                  <Briefcase />
                  <Text>{manager?.jobTitle},</Text>
                  <Text>{manager?.department}</Text>
                </Group>
                <Group>
                  <Phone />
                  <Text>{manager?.businessPhones}</Text>
                </Group>
                <Text>Submitted by: {user?.displayName}</Text>
              </Stack>
            </Box>
          )}
        </Box>
        <Divider my="sm" label="Ticket Details" labelProps={{ size: 'lg' }} />
        
              <TextInput label="Ticket Subject" {...form.getInputProps('ticketSubject')} />

              <RadioGroup
                  {...form.getInputProps('impact')}
                  label="Impact"
                  required
                  color="blue"
                  size="sm"
              >
                  <Radio value="Impact1" label="Impact1" />
                  <Radio value="Impact2" label="Impact2" />
              </RadioGroup>

              <RadioGroup
                  {...form.getInputProps('urgency')}
                  label="Urgency"
                  required
                  color="blue"
                  size="sm"
              >
                  <Radio value="Urgency1" label="Urgency1" />
                  <Radio value="Urgency2" label="Urgency2" />
              </RadioGroup>

              <RadioGroup
                  {...form.getInputProps('category')}
                  label="Category"
                  my="sm"
                  required
                  color="blue"
                  size="sm"
                  orientation="vertical"
              >
                  <Radio value="Technical Support (LH)" label="Hardware Problem (PC, laptop, Tap&Go, monitor, printer)" />
                  <Radio value="Epic Application Support" label="Epic Application Problem" />
                  <Radio value="Applications (LH)" label="Applications Problem (SAP, Meditech, Office)" />
                  <Radio value="Security (LH)" label="Security (virus, spam, malware)" />
                  <Radio value="Network" label="Connectivitiy (wifi, internet, network)" />
              </RadioGroup>
              {form.values['category'] !== 'Network' && (
                  <Select searchable
                      my="sm"
                      label="Tell us more about your issue"
                      nothingFound="No Subcategories Found"
                      {...form.getInputProps('subcategory')}
                      data={[{ value: 'Subcat1', label: 'Subcat1' },{ value: 'Subcat2', label: 'Subcat2'}]}
                  />
              )}
              {form.values['category'] === 'Technical Support (LH)' &&
                  <Box>
                      <TextInput label='Device Name'  {...form.getInputProps('deviceName')} />
                      <TextInput label='Device Location'  {...form.getInputProps('deviceLocation')} />
                  </Box>
              
              }
              <TextInput label="Preferred Contact Method - Email, Mobile Number, or Extension for call back" {...form.getInputProps('preferredContact')} />
              <TextInput label="If we can't reach you today when is the best time to call back?" {...form.getInputProps('callbackTime')} />

              <RadioGroup required label="Is this a recurring issue?" {...form.getInputProps('recurringIssue')} orientation="vertical">
                  <Radio value="First Time" label="First Time - this is the first time it has happened" />
                  <Radio value="Recurring First Ticket" label="Recurring First Ticket - This has happened before but this is my first ticket" />
                  <Radio value="Recurring Repeat Ticket" label="Recurring First Ticket - This has happened before and I have created other tickets" />
              </RadioGroup>

              <Textarea required label="Request Details" {...form.getInputProps('requestDetails')} error={form.values['requestDetails'].length < 100 ? 'Details must be greater than 100 characters.' : ''}/>

        {form.values['requestDetails'].length >= 100  && (
          <Button my="sm" type="submit" loading={loadingState}>
            Submit Ticket
          </Button>
        )}
      </form>
    </>
  );
};

export default DeviceRequest;